// A small modal that asks "are you sure?" before something that can't be undone. It is the
// native `<dialog>` in the top layer, so the browser supplies the backdrop, the focus trap and
// the inert page behind it. Escape and a click on the backdrop cancel; focus starts on Cancel
// and goes back to where it was when the dialog closes.

import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The shared modal shell: the native `<dialog>` opened modally, with Escape and a click on the
 * backdrop calling `onCancel` (unless `busy`), focus starting on `initialFocus` and returning
 * to where it was when the dialog closes. The caller supplies the card inside it.
 */
export function ModalDialog({
  labelledBy,
  busy = false,
  onCancel,
  initialFocus,
  children,
}: {
  labelledBy: string;
  busy?: boolean;
  onCancel: () => void;
  initialFocus?: { current: HTMLElement | null };
  children: ReactNode;
}): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    const before = document.activeElement;
    if (dialog !== null && !dialog.open) {
      dialog.showModal();
    }
    initialFocus?.current?.focus();
    return () => {
      dialog?.close();
      if (before instanceof HTMLElement && before.isConnected) {
        before.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="confirmDialog"
      aria-labelledby={labelledBy}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) {
          onCancel();
        }
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onCancel();
        }
      }}
    >
      {children}
    </dialog>
  );
}

export function ConfirmDialog({
  title,
  name,
  detail,
  confirmLabel,
  onConfirm,
  onCancel,
  problemFor,
}: {
  title: string;
  /** The thing being confirmed, shown on its own line; long names wrap and are cut at 3 lines. */
  name: string;
  detail: string;
  confirmLabel: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  problemFor: (error: unknown) => string;
}): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    try {
      await onConfirm();
    } catch (error) {
      setProblem(problemFor(error));
      setBusy(false);
    }
  };

  return (
    <ModalDialog
      labelledBy="confirmDialog-title"
      busy={busy}
      onCancel={onCancel}
      initialFocus={cancelRef}
    >
      <div className="confirmDialog__card">
        <h2 className="confirmDialog__title" id="confirmDialog-title">
          {title}
        </h2>
        <p className="confirmDialog__body">
          <span className="confirmDialog__name" title={name}>
            {name}
          </span>
          {detail}
        </p>
        {problem === null ? null : (
          <p className="artifact__problem" role="alert">
            {problem}
          </p>
        )}
        <div className="confirmDialog__buttons">
          <button
            ref={cancelRef}
            type="button"
            className="button"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button button--danger"
            disabled={busy}
            onClick={() => void confirm()}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </ModalDialog>
  );
}
